import React from 'react';

import { IconTypes } from '@eika/icon';

import { NoteField } from '../../components/NoteField';
import AccountField from '../../components/transaction/AccountField';
import AmountLimitField from '../../components/transaction/AvtaleGiro/AmountLimitField';
import KidField from '../../components/transaction/AvtaleGiro/KidField';
import { AvtaleGiroActionType } from '../../components/transaction/AvtaleGiro/form/actions';
import useFormContext from '../../components/transaction/AvtaleGiro/form/useFormContext';
import ReceiverField from '../../components/transaction/Payment/ReceiverField';
import { SelectedReceiver } from '../../components/transaction/SelectedReceiver';
import type { Account } from '../../generated-types';
import { usePaymentAccountContext } from '../../hooks/useAccountState';
import useTranslation from '../../hooks/useTranslation';
import { useSigningContext } from '../../signingContext/signingContext';

interface Props {
    accounts: Account[];
    accountState: { isLoading: boolean; refetch: () => void };
    receiver?: SelectedReceiver;
    className?: string;
    openAmountLimitsModal?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

const AvtaleGiroEditableDetails: React.FC<Props> = ({
    receiver,
    accountState,
    accounts,
    className,
    openAmountLimitsModal = [false, () => null],
}) => {
    const translated = useTranslation();
    const { state, handleChange, errors, curryChange } = useFormContext();
    const { signingOperationPending } = useSigningContext();
    const { targetFromAccount } = usePaymentAccountContext();
    return (
        <div className={className}>
            <li className="avtalegiro-editable-detail-list__item">
                <AccountField
                    id="avtalegiroFromAccount"
                    placeholder={translated.payFromAccount}
                    buttonLabel={translated.from}
                    title={translated.paymentFrom}
                    accountNumber={state.fromAccount}
                    account={targetFromAccount}
                    accounts={accounts ?? []}
                    error={errors.accountId}
                    onChange={curryChange(AvtaleGiroActionType.FROM_ACCOUNT_SELECTED)}
                    isLoading={accountState.isLoading}
                    disabled={false}
                    refresh={accountState.refetch}
                    showModal={state.showFromAccounts}
                    setShowModal={(showModal: boolean) => {
                        handleChange({
                            type: showModal
                                ? AvtaleGiroActionType.SHOW_FROM_ACCOUNTS
                                : AvtaleGiroActionType.HIDE_FROM_ACCOUNTS,
                        });
                    }}
                    locked={signingOperationPending}
                />
            </li>
            <li className="avtalegiro-editable-detail-list__item">
                <ReceiverField receiver={receiver} locked onChange={() => null} error={errors.toAccount} />
            </li>
            <li className="avtalegiro-editable-detail-list__item">
                <KidField
                    toAccount={state.toAccount}
                    kid={state.kid}
                    locked
                    labelText={translated.avtaleGiroAgreement.kidField}
                    placeholder={translated.avtaleGiroAgreement.kidPlaceholder}
                    error={errors.kid}
                    onChange={(value) => value && handleChange({ type: AvtaleGiroActionType.SET_KID, payload: value })}
                />
            </li>
            <li className="avtalegiro-editable-detail-list__item">
                <NoteField
                    note={state.note}
                    onChange={curryChange(AvtaleGiroActionType.NOTE)}
                    error={errors.note}
                    labelText={translated.avtaleGiroAgreement.ownText}
                    placeholder={translated.avtaleGiroAgreement.ownText}
                    locked={signingOperationPending}
                    icon={IconTypes.PENN_24}
                    forceDisableKid
                />
            </li>
            <AmountLimitField
                transactionLimit={state.transactionLimit}
                monthlyLimit={state.monthlyLimit}
                error={{ transaction: errors.transactionLimit, monthly: errors.monthlyLimit }}
                onChange={curryChange(AvtaleGiroActionType.TRANSACTION_AMOUNT_LIMIT)}
                onChangeMonthlyLimit={curryChange(AvtaleGiroActionType.MONTHLY_AMOUNT_LIMIT)}
                icon={IconTypes.PENN_24}
                locked={signingOperationPending}
                externalOpen={openAmountLimitsModal}
                className="avtalegiro-editable-detail-list__item"
            />
        </div>
    );
};

export default AvtaleGiroEditableDetails;
